import CodeMirror from '@uiw/react-codemirror';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useFilePicker } from 'use-file-picker';

import { Button } from '@components';

import styles from './StorageConfig.module.scss';

const languageNamespace = 'storage-config';

type FileContent = {
  content: string;
};

type FilesSelectedEvent = {
  plainFiles: File[];
  filesContent: FileContent[];
  errors: string[];
};

interface StorageConfigProps {
  storageKey: string;
  previousKey: string;
  activeConfig: Record<string, unknown>;
  getOriginalConfig: () => Record<string, string> | Record<string, unknown>;
  description: string;
  shouldStringifyValues: boolean;
}

const downloadConfig = (content: BlobPart, fileName: string, contentType: string) => {
  const a = document.createElement('a');
  const file = new Blob([content], { type: contentType });
  a.href = URL.createObjectURL(file);
  a.download = fileName;
  a.click();
};

export const stringifyValues = (obj: Record<string, unknown>) =>
  Object.keys(obj).reduce((acc, key) => ({ ...acc, [key]: `${obj[key]}` }), {});

const exportHandler = (config: string) => {
  const data = JSON.parse(config);
  const fileName = 'bbo_config';
  downloadConfig(JSON.stringify(data, null, 2), `${fileName}.json`, 'application/json');
};

const submitHandler = (config: string, key: string, shouldStringify: boolean) => {
  const parsed = JSON.parse(config);
  const values = shouldStringify ? stringifyValues(parsed) : parsed;
  const stringified = JSON.stringify(values);
  localStorage.setItem(key, stringified);
  window.location.reload();
};

export const StorageConfig = ({
  activeConfig,
  storageKey,
  previousKey,
  getOriginalConfig,
  description,
  shouldStringifyValues,
}: StorageConfigProps) => {
  const [config, setConfig] = useState(JSON.stringify(activeConfig, null, 2));
  const {
    formState: { errors },
    setError,
  } = useForm();

  const { t } = useTranslation(languageNamespace);

  const handleFileSelection = (filesContent: FileContent[], errors: string[]) => {
    if (!filesContent.length) {
      console.error('Errors occurred during file selection:', errors);
      return;
    }
    try {
      setError('config', { type: 'manual', message: '' });
      setConfig(filesContent[0].content);
    } catch (_e) {
      setError('config', {
        type: 'manual',
        message: `${t('notImported')} ${(_e as Error).message}`,
      });
    }
  };

  const { openFilePicker } = useFilePicker({
    accept: '.json',
    multiple: false,
    onFilesSelected: ({ filesContent, errors }: FilesSelectedEvent) => {
      handleFileSelection(filesContent, errors);
    },
  });

  const handleExport = () => {
    try {
      setError('config', { type: 'manual', message: '' });
      exportHandler(config);
    } catch (_e) {
      setError('configFile', {
        type: 'manual',
        message: (_e as Error).message,
      });
    }
  };

  const handleSubmit = () => {
    try {
      JSON.parse(config);
      setError('config', { type: 'manual', message: '' });
    } catch (_e) {
      setError('config', {
        type: 'manual',
        message: `${t('notImported')} ${(_e as Error).message}`,
      });
    }
    submitHandler(config, storageKey, shouldStringifyValues);
  };

  const handleResetPrevious = () => {
    setConfig(JSON.stringify(activeConfig, null, 2));
  };

  const handleResetDefault = () => {
    const updatedConfig = JSON.stringify(getOriginalConfig(), null, 2);
    setError('config', { type: 'manual', message: '' });
    setConfig(updatedConfig);
    localStorage.removeItem(storageKey);
    localStorage.removeItem(previousKey);
    window.location.reload();
  };

  const handleChangeConfig = (newConfig: string) => {
    try {
      JSON.parse(newConfig);
      setError('config', { type: 'manual', message: '' });
    } catch (_e) {
      setError('config', {
        type: 'manual',
        message: `${t('notImported')} ${(_e as Error).message}`,
      });
    }
    setConfig(newConfig);
  };

  return (
    <div>
      <div className={styles.container}>
        <div className={styles.topButtonRow}>
          <Button
            className={styles.buttonSpacing}
            small
            inverted
            onClick={openFilePicker}
            text={t('importConfig')}
          />
          <Button small inverted onClick={handleExport} text={t('exportConfig')} />
        </div>

        <p className={styles.description}>{description}</p>

        <CodeMirror height="100%" value={config} onChange={handleChangeConfig} theme="light" />

        <p className={styles.errorMessage}>
          {typeof errors.config?.message === 'string' ? t(errors.config.message) : ''}
        </p>

        <div>
          <Button
            className={styles.buttonSpacing}
            small
            onClick={() => handleSubmit()}
            text={t('updateConfig')}
          />

          <Button
            className={styles.buttonSpacing}
            small
            onClick={handleResetPrevious}
            text={t('undoUnsavedChanges')}
          />

          <Button small onClick={handleResetDefault} text={t('resetToDefaults')} />
        </div>
      </div>
    </div>
  );
};
